import { useState, useRef, useCallback, useEffect } from 'react';
// @ts-ignore
import { GoogleGenAI, Modality } from '@google/genai';

/**
 * useLiveAPI — Version avec détection ToolCall corrigée
 */

export function useLiveAPI(
  modelName: string,
  customApiKey?: string,
  voiceName: string = "Aoede",
  setCurrentTranscript?: (text: string) => void
) {
  const [isConnected, setIsConnected] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const aiRef = useRef<any>(null);
  const isToolPendingRef = useRef(false);

  useEffect(() => {
    const apiKey = customApiKey || process.env.REACT_APP_GEMINI_API_KEY || "AIzaSyCm_t6RmVzXCpzn70RETUHd8z3I5B1xz7s";
    if (apiKey) {
      aiRef.current = new GoogleGenAI({ apiKey });
    }
  }, [customApiKey]);

  const sessionRef = useRef<any>(null);
  const micContextRef = useRef<AudioContext | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const playbackContextRef = useRef<AudioContext | null>(null);
  const nextPlayTimeRef = useRef<number>(0);
  const isConnectingRef = useRef(false);
  // Génération de connexion : incrémentée à chaque disconnect. Permet à onopen
  // de détecter qu'un disconnect a eu lieu PENDANT le handshake et d'abandonner
  // proprement au lieu de laisser une session orpheline (micro + WebSocket actifs).
  const generationRef = useRef(0);

  // ============================================================
  //     disconnect : Ferme la session Gemini, libère micro + audio
  // Nettoie le ScriptProcessor (micro), l'AudioContext (playback),
  // ferme la session WebSocket Live, remet isConnected = false
  // ============================================================
  const disconnect = useCallback(() => {
    console.log('[LiveAPI] 🛑 Déconnexion...');
    generationRef.current += 1;
    isConnectingRef.current = false;
    setIsConnected(false);
    setIsProcessing(false);

    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }
    if (micContextRef.current) {
      micContextRef.current.close().catch(() => {});
      micContextRef.current = null;
    }
    if (playbackContextRef.current) {
      playbackContextRef.current.close().catch(() => {});
      playbackContextRef.current = null;
    }
    if (sessionRef.current) {
      try { sessionRef.current.close(); } catch (e) { /* ignore */ }
      sessionRef.current = null;
    }
  }, []);

  // ============================================================
  // connect : Ouvre la session WebSocket LIVE avec Gemini
  // Envoie le micro en temps réel (PCM audio 16kHz → 24000Hz),
  // initialise AudioContext pour le playback de la réponse audio,
  // écoute les ToolCall, AI speech, User speech transcriptions.
  // toolsConfig = les 10 functionDeclarations de useGeminiVoiceOrdering
  // customSystemInstruction = le system prompt (menu, panier, trios...)
  // ============================================================
  const connect = useCallback(async (toolsConfig?: any[], customSystemInstruction?: string, voiceNameOverride?: string) => {
    if (isConnected || isConnectingRef.current || !aiRef.current) return;
    isConnectingRef.current = true;
    const myGen = generationRef.current; // capture la génération au départ du handshake

    setIsProcessing(true);
    playbackContextRef.current = new AudioContext({ sampleRate: 24000 });
    nextPlayTimeRef.current = 0;

    const formattedModel = modelName.startsWith('models/') ? modelName : `models/${modelName}`;
    const effectiveVoiceName = voiceNameOverride || voiceName;

    try {
      const sessionPromise = aiRef.current.live.connect({
        model: formattedModel,
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: effectiveVoiceName } },
          },
          systemInstruction: customSystemInstruction || "Tu es AI-LYN, assistant vocal en français.",
          inputAudioTranscription: {},
          outputAudioTranscription: {},
          tools: toolsConfig || []
        },
        callbacks: {
          onopen: async () => {
            // disconnect() appelé pendant le handshake ? → on ferme la session
            // naissante au lieu d'ouvrir le micro (sinon session orpheline).
            if (myGen !== generationRef.current) {
              console.log('[LiveAPI] ⚠️ Connexion annulée pendant le handshake — fermeture de la session naissante');
              sessionPromise.then((s: any) => { try { s.close(); } catch (e) { /* ignore */ } });
              return;
            }
            console.log('[LiveAPI] ✅ WebSocket Ouvert');
            isConnectingRef.current = false;
            setIsConnected(true);
            setIsProcessing(false);

            try {
              micStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
              micContextRef.current = new AudioContext({ sampleRate: 16000 });
              const source = micContextRef.current.createMediaStreamSource(micStreamRef.current);
              const processor = micContextRef.current.createScriptProcessor(4096, 1, 1);
              scriptProcessorRef.current = processor;
              processor.onaudioprocess = async (e) => {
                if (isToolPendingRef.current) return;
                const channelData = e.inputBuffer.getChannelData(0);
                const pcm16 = new Int16Array(channelData.length);
                for (let i = 0; i < channelData.length; i += 1) {
                  const s = Math.max(-1, Math.min(1, channelData[i]));
                  pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
                }
                const sessionNow = await sessionPromise;
                if (sessionNow && (sessionNow as any).sendRealtimeInput) {
                  const bytes = new Uint8Array(pcm16.buffer);
                  let binary = '';
                  for (let j = 0; j < bytes.byteLength; j += 1) { binary += String.fromCharCode(bytes[j]); }
                  const base64Data = btoa(binary);
                  (sessionNow as any).sendRealtimeInput({ audio: { data: base64Data, mimeType: 'audio/pcm;rate=16000' } });
                }
              };
              source.connect(processor);
              processor.connect(micContextRef.current.destination);
            } catch (err) { console.error('[LiveAPI] ❌ Erreur micro:', err); disconnect(); }
          },
          onmessage: async (message: any) => {
            // 1. DÉTECTION DES ACTIONS (TOOL CALLS)
            if (message.toolCall) {
              const { functionCalls } = message.toolCall;
              if (functionCalls?.length > 0) {
                isToolPendingRef.current = true;
                const sessionNow = await sessionPromise;
                functionCalls.forEach((call: any) => {
                  console.log(`[LiveAPI] 🤖 ACTION DÉTECTÉE : ${call.name}`, call.args);
                  window.dispatchEvent(new CustomEvent('gemini-function-call', {
                    detail: {
                      name: call.name,
                      args: call.args,
                      id: call.id,
                      session: sessionNow
                    }
                  }));
                });
                setTimeout(() => { isToolPendingRef.current = false; }, 1500);
              }
            }

            // 2. DÉTECTION DU CONTENU (AUDIO / TEXTE)
            const parts = message.serverContent?.modelTurn?.parts || [];

            // 🤖 Ce que l'IA est en train de répondre
            const outputTranscription = message.serverContent?.outputTranscription;
            if (outputTranscription?.text) {
              console.log('[LiveAPI] 🤖 Agent:', outputTranscription.text);
              if (setCurrentTranscript) {
                setCurrentTranscript(outputTranscription.text);
              }
              window.dispatchEvent(new CustomEvent('gemini-ai-speech', {
                detail: { text: outputTranscription.text }
              }));
            }

            // 🗣️ Ce que l'utilisateur est en train de dire (Speech-to-Text)
            const inputTranscription = message.serverContent?.inputTranscription;
            if (inputTranscription?.text) {
              console.log('[LiveAPI] 👤 Utilisateur:', inputTranscription.text);
              window.dispatchEvent(new CustomEvent('gemini-user-speech', {
                detail: { text: inputTranscription.text }
              }));
            }

            parts.forEach((part: any) => {
              const base64Audio = part.inlineData?.data;
              if (base64Audio && playbackContextRef.current) {
                const binary = atob(base64Audio);
                const data = new Int16Array(binary.length / 2);
                for (let i = 0; i < data.length; i += 1) {
                  // eslint-disable-next-line no-bitwise
                  data[i] = binary.charCodeAt(i * 2) | (binary.charCodeAt(i * 2 + 1) << 8);
                }
                const ctx = playbackContextRef.current;
                const audioBuffer = ctx.createBuffer(1, data.length, 24000);
                audioBuffer.getChannelData(0).set(Array.from(data).map(v => v / 32768.0));
                const sourceNode = ctx.createBufferSource();
                sourceNode.buffer = audioBuffer;
                sourceNode.connect(ctx.destination);
                if (nextPlayTimeRef.current < ctx.currentTime) nextPlayTimeRef.current = ctx.currentTime;
                sourceNode.start(nextPlayTimeRef.current);
                nextPlayTimeRef.current += audioBuffer.duration;
              }

              if (part.text && setCurrentTranscript) {
                console.log(`[LiveAPI] 🤖 Transcription IA : ${part.text}`);
                setCurrentTranscript(part.text);
                window.dispatchEvent(new CustomEvent('gemini-ai-speech', {
                  detail: { text: part.text }
                }));
              }
            });

            // Compatibilité avec les anciennes versions si toolCall est dans parts
            const legacyCalls = parts.filter((p: any) => p.functionCall);
            if (legacyCalls.length > 0) {
              const sessionNow = await sessionPromise;
              legacyCalls.forEach((p: any) => {
                console.log(`[LiveAPI] 🤖 ACTION LEGACY : ${p.functionCall.name}`);
                window.dispatchEvent(new CustomEvent('gemini-function-call', {
                  detail: { ...p.functionCall, session: sessionNow }
                }));
              });
            }

            if (message.serverContent?.interrupted && playbackContextRef.current) {
                 try { playbackContextRef.current.close(); } catch(e) { /* ignore */ }
                 playbackContextRef.current = new AudioContext({ sampleRate: 24000 });
                 nextPlayTimeRef.current = 0;
            }
          },
          onerror: (err: any) => { console.error('[LiveAPI] ❌ Erreur:', err); disconnect(); },
          onclose: (event: any) => { console.log('[LiveAPI] 🔌 WebSocket FERMÉ:', event); disconnect(); }
        }
      });
      // sessionRef.current = await sessionPromise;   // ancien code (pas de guard d'annulation)
      const session = await sessionPromise;
      if (myGen !== generationRef.current) {
        try { session.close(); } catch (e) { /* ignore */ }
        return;
      }
      sessionRef.current = session;
    } catch (err) {
      console.error('[LiveAPI] ❌ Échec connexion:', err);
      disconnect();
    }
  }, [isConnected, modelName, voiceName, disconnect, setCurrentTranscript]);

  // Cleanup au démontage (hot-reload, navigation) : sans ça, une session active
  // survit au remontage du composant et continue de streamer le micro vers Gemini
  // → sessions orphelines qui s'accumulent à chaque hot-reload en dev.
  useEffect(() => () => { disconnect(); }, [disconnect]);

  const sendToolResponse = useCallback((functionResponses: any[]) => {
    if (sessionRef.current && sessionRef.current.sendToolResponse) {
      console.log('[LiveAPI] 📤 Envoi de la réponse de l\'outil:', functionResponses);
      sessionRef.current.sendToolResponse({ functionResponses });
    } else {
      console.error('[LiveAPI] ❌ Impossible d\'envoyer la réponse de l\'outil, session non prête.');
    }
  }, []);

  return { isConnected, isProcessing, connect, disconnect, sendToolResponse };
}
